import React, { useState, useEffect, useCallback, useRef } from 'react';
import { fetchSupportArticleName } from '../../functions/fetchSupport';
import language from '../../../utils/LanguageSettings';
import translations from '../../../text/get-started-translations.json';

// Custom hook for debouncing values
const useDebounce = (value, delay) => {
    const [debouncedValue, setDebouncedValue] = useState(value);

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedValue(value);
        }, delay);
        return () => {
            clearTimeout(handler);
        };
    }, [value, delay]);

    return debouncedValue;
};


interface SupportSearchSuggestionProps {
    query: string;
    isSearch: boolean;
    onData:(data: string) => void
}

const SupportSearchSuggestion: React.FC<SupportSearchSuggestionProps> = ({query, isSearch, onData}) => {
    const [results, setResults] = useState([]);
    const debouncedQuery = useDebounce(query, 1000);
    const [loading, setLoading] = useState<boolean>(isSearch);
    const pageTranslations = translations[language];

    const fetchResults = useCallback(async (searchQuery) => {
        if (searchQuery) {
            try {
                const response = await fetchSupportArticleName();
                let targetData = [];
                response.forEach(element => {
                    if(element?.name?.toLocaleLowerCase().indexOf(searchQuery?.toLocaleLowerCase()) > -1){
                        targetData.push(element);
                    }
                });
                setResults(targetData);
                setLoading(false);
            } catch (error) {
                setResults([]);
            }
        } else {
            setResults([]);
        }
    }, []);

    const ref = useRef(null);

    useEffect(() => {
        const handleKeyDown = (event) => {
            if (ref.current && !ref.current.contains(event.target)) {
                setResults([]);
            }
        };

        const handleClick = (event) => {
            if (ref.current && !ref.current.contains(event.target)) {
                setResults([]);
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        document.addEventListener('click', handleClick);

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
            document.removeEventListener('click', handleClick);
        };
    }, []);

    const onClick = (keyword: string) => {
        onData(keyword);
        setResults([]);
    }

    useEffect(() => {
        if(isSearch){
            setLoading(isSearch);
            fetchResults(debouncedQuery);
        }
    }, [debouncedQuery, fetchResults,isSearch]);

    if(isSearch && loading && query.length > 0){
        return (
        <>
            <ul ref={ref}>
                <li>{pageTranslations.supportSearchLoading}</li>
            </ul>
        </>
    );
    }

    return (
        <>
            {results.length > 0 && (
                <ul ref={ref}>
                    {results.map((result, index) => (
                        <li key={index} onClick={()=>onClick(result?.name)}>
                            {result?.name}
                        </li>
                    ))}
                </ul>
            )}
            {results.length === 0 && query.length > 0 && (
                <></>
            )}
        </>
    );
};

export default SupportSearchSuggestion;
